import React from "react";
import LocalizedText from '../presentational/LocalizedText';
import useTranslate from "../../hooks/useTranslate";
import { Button, FormGroup, Input, Intent } from "@careevolution/design-system";
import { useResendCodeTimer } from "./hooks";

interface VerifyPhoneProps {
  serverError: string;
  isLoading: boolean;
  emailOrPhone: string;
  resendWait: number;
  verificationCode: string;
  attemptVerifyPhone: (verificationCode: string) => void;
  resendVerificationCode: () => void;
  setVerificationCode: (verificationCode: string) => void;
}

const VerifyPhone: React.FC<VerifyPhoneProps> = ({
  serverError,
  isLoading,
  emailOrPhone,
  resendWait,
  verificationCode,
  attemptVerifyPhone,
  resendVerificationCode,
  setVerificationCode,
}) => {
  const {
    onResendCode,
    waitCountdown,
    isCodeResent,
    isResendCodeReady,
  } = useResendCodeTimer(resendVerificationCode, resendWait)

  const translations = useTranslate(["participant-login-verification-code-label", "participant-login-next-label"])

  const handleSubmit = () => {
    attemptVerifyPhone(verificationCode)
    return false
  }

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value)
  }

  return (
    <form className="login-action verify-phone" onSubmit={handleSubmit}>
      <h1><LocalizedText textKey="participant-login-verify-phone-title" /></h1>
      <p><LocalizedText textKey="participant-login-verify-phone-info" values={{ emailOrPhone: emailOrPhone }} /></p>
      {
        serverError && (
          <p className="server-error">{serverError}</p>
        )
      }
      <FormGroup
        className="login-field"
        label={translations["participant-login-verification-code-label"]}
      >
        <Input
          autoComplete="one-time-code"
          className="autofocus"
          disabled={isLoading}
          onChange={handleVerificationCodeChange}
          id="phoneVerificationCode"
          type="tel"
          value={verificationCode}
          placeholder="123456"
          fullWidth={true}
          htmlAttributes={{
            maxLength: 6,
            pattern: "[0-9]{6}",
            spellCheck: false,
            autoCorrect: "off",
            autoCapitalize: "none",
          }}
        />
      </FormGroup>
      <Button
        onClick={handleSubmit}
        disabled={!verificationCode}
        className="primary-action"
        width="100%"
        intent={Intent.primary}
        loading={isLoading}
        type='submit'
      >
        <span dangerouslySetInnerHTML={{ __html: translations["participant-login-next-label"] }} />
      </Button>
      <div aria-live="polite">
        {
          isResendCodeReady && (
            <p className="sub-action">
              <Button
                onClick={onResendCode}
                intent={Intent.secondary}
              >
                <LocalizedText textKey="participant-login-resend-code-label" />
              </Button>
            </p>
          )
        }
        {
          isCodeResent && !isResendCodeReady && (
            <p className="sub-action"><LocalizedText textKey="participant-login-verify-code-resent" /></p>
          )
        }
        {
          !isResendCodeReady && (
            <p className="sub-action"><LocalizedText textKey="participant-login-verify-resend-available-format" values={{ resendWait: waitCountdown }} /></p>
          )
        }
      </div>
    </form>
  )
}

export default VerifyPhone